import type {
  Communication,
  Organization,
  Patient,
  Practitioner,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import { z } from 'zod'
import * as codeSystems from '#src/codeSystems.ts'
import { elationSandboxIdentifier } from '#src/elation.ts'
import { EntityType } from '#src/models/elation/entityType.ts'
import { createReference } from '#src/references.ts'
import { build } from '#src/utils.ts'

const sendToContactSchema = z.object({
  id: z.number(),
  first_name: z.string(),
  last_name: z.string(),
  org_name: z.string(),
  specialties: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    })
  ),
  npi: z.string(),
})

const faxStatusSchema = z.enum(['pending', 'success', 'failed'])

const deliveryMethodSchema = z.enum([
  'emr',
  'passport',
  'fax',
  'email',
  'printed',
  'direct',
])

const letterTypeSchema = z.enum([
  'patient_initiated',
  'patient',
  'referral',
  'provider',
])

const attachmentTypeSchema = z.enum([
  'Entity',
  'RefPanel',
  'PatientDemog',
  'ImmHistory',
  'MedsList',
  'WhoHeightChart',
  'WhoWeightChart',
  'WhoHcChart',
  'CdcHeightChart',
  'CdcWeightChart',
  'CdcBmiChart',
  'CompMedsList',
  'CdcHcChart',
])

const attachmentSchema = z.object({
  id: z.number(),
  attachment_type: attachmentTypeSchema.nullable(),
  entity_type: z.nativeEnum(EntityType).nullable(),
  entity_id: z.number().nullable(),
  entity_description: z.string().nullable(),
  entity_date: z.string().nullable(),
})

export const letterSchema = z.object({
  id: z.number(),
  send_to_contact: sendToContactSchema.nullable(),
  send_to_name: z.string(),
  fax_to: z.string(),
  display_to: z.string(),
  to_number: z.string(),
  subject: z.string(),
  body: z.string(),
  fax_status: faxStatusSchema,
  fax_attachments: z.boolean(),
  delivery_method: deliveryMethodSchema,
  failure_unacknowledged: z.boolean(),
  direct_message_to: z.string(),
  email_to: z.string(),
  viewed_at: z.string().nullable(),
  send_to_elation_user: z.number(),
  delivery_date: z.string().nullable(),
  with_archive: z.boolean(),
  is_processed: z.boolean(),
  letter_type: letterTypeSchema,
  attachments: z.array(attachmentSchema),
  sign_date: z.string().nullable(),
  signed_by: z.number().nullable(),
  tags: z.array(z.unknown()),
  document_date: z.string(),
  patient: z.number(),
  practice: z.number(),
  created_date: z.string().optional().nullable(),
  deleted_date: z.string().optional().nullable(),
})

export type Letter = z.infer<typeof letterSchema>

/**
 * The date a letter/fax should be attributed to on the timeline. Elation only
 * populates `delivery_date` once a fax is confirmed delivered, so fall back to
 * the sign date and finally `document_date` (always present). Without this the
 * Communication has a null `sent` and the timeline can't place the fax.
 */
export function letterSentDate(letter: {
  delivery_date?: string | null
  sign_date?: string | null
  document_date?: string | null
}): string | undefined {
  return (
    letter.delivery_date ??
    letter.sign_date ??
    letter.document_date ??
    undefined
  )
}

const PARTICIPATION_MODE_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/v3-ParticipationMode'

const deliveryMethodToParticipationMode: Record<
  z.infer<typeof deliveryMethodSchema>,
  {
    code: string
    display: string
  }
> = {
  emr: {
    code: 'ELECTRONIC',
    display: 'electronic data',
  },
  passport: {
    code: 'ELECTRONIC',
    display: 'electronic data',
  },
  fax: {
    code: 'FAXWRIT',
    display: 'telefax',
  },
  email: {
    code: 'EMAILWRIT',
    display: 'email',
  },
  printed: {
    code: 'TYPEWRIT',
    display: 'typewritten',
  },
  direct: {
    code: 'ELECTRONIC',
    display: 'electronic data',
  },
}

export function toFhir({
  letter,
  patient,
  recipient,
}: {
  letter: Letter
  patient: Patient
  recipient?: Practitioner | Organization
}): Communication {
  const participationMode =
    deliveryMethodToParticipationMode[letter.delivery_method]

  const sendToName = letter.send_to_name.trim()
  const displayTo = letter.display_to.trim()
  let sendToString = ''
  if (sendToName) {
    sendToString += sendToName
  }
  if (displayTo && displayTo !== sendToName) {
    if (sendToString) {
      sendToString += ' - '
    }
    sendToString += displayTo
  }

  const payload = compact([
    sendToString && {
      contentString: sendToString,
    },
    letter.subject && {
      contentString: letter.subject,
    },
    letter.body && {
      contentString: letter.body,
    },
  ])

  return build('Communication', {
    identifier: [elationSandboxIdentifier(letter.id.toString())],
    status: letter.is_processed ? 'completed' : 'in-progress',
    subject: createReference(patient),
    sent: letterSentDate(letter),
    received: letter.viewed_at ?? undefined,
    recipient: recipient ? [createReference(recipient)] : undefined,
    payload,
    category: [
      {
        coding: [
          {
            system:
              'http://terminology.hl7.org/CodeSystem/communication-category',
            code: 'notification',
            display: 'Notification',
          },
        ],
      },
    ],
    medium: [
      {
        coding: [
          {
            system: PARTICIPATION_MODE_SYSTEM,
            code: participationMode.code,
            display: participationMode.display,
          },
        ],
      },
    ],
    extension: [
      {
        url: codeSystems.EXTERNAL_RESOURCE_JSON_SYSTEM,
        valueString: JSON.stringify(letter),
      },
    ],
  })
}
